import { useRef } from 'react'
import useConfirmOrderMutation from '../../graphQL/mutations/confirmOrder'
import useCreateOrderMutation from '../../graphQL/mutations/createOrder'
import { useLazyGetOrder } from '../../graphQL/queries/order'
import type { SessionDataArgs } from '../../graphQL/queries/getSession/types'
import type { IOrderDetail } from '../../graphQL/queries/order/types'
import {
  clearOrderCreation,
  setOrderConfirmed,
  setOrderConfirming,
  setOrderCreated,
  setOrderCreating,
  setOrderCreationFailed,
  setOrderCreationPollResult,
  setOrderOutcomeUnknown,
} from '../../redux/slices/orders'
import {
  deriveOrderOutcome,
  resolveOrderConfirmation,
  type OrderOutcome,
} from '../../utils/orderConfirmation'
import { ORDER_POLL_FIRST_LOOK_MS } from '../../redux/slices/orders/types'
import { postStephook } from '../../services/postCreateOrder'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import {
  getFailedCheckoutRules,
  getServerErrorDescription,
  handleOrdersFetchError,
  isAmbiguousOrderMatchError,
  isBadRequestError,
} from './ordersHookHelpers'
import useOrderConfirmationConfigGuards from './useOrderConfirmationConfigGuards'

const FUNCTION_NAME = 'useOrderConfirmation'

/**
 * The statuses `confirmOrder`'s own filter will match.
 *
 * `{ status: { $in: [CREATED, SUGGESTED] } }` server-side, and mirrored here
 * because calling the mutation for anything else cannot succeed. An order in
 * `WAITING_CALLBACK` is the case that matters: it is progressing normally, the
 * filter will not find it, and a call would turn a healthy order into a
 * spurious failure.
 */
const CONFIRMABLE_STATUSES = ['CREATED', 'SUGGESTED']

/**
 * Creates the order from the webview, confirms it where that applies, and
 * polls as a safety net.
 *
 * **Only reachable for a storefront that authored
 * `options.inWebviewOrderConfirmation`.** The branch is in
 * `useCreateOrderHook`; nothing here runs for the rest of the estate, which
 * keeps its stephook-then-redirect exit untouched.
 *
 * The shape of the flow is that **the created order's status is the mechanism
 * selector** (M1-B Jot Down D-5), not config. The CREATE_ORDER add-on returns
 * `WAITING_CALLBACK` when it has `hasCallback` and `CREATED` — or `SUGGESTED`,
 * where the client system changed the order — when it does not, so the answer
 * to "how does this tenant confirm?" is already in the response. Config
 * decides only whether confirmation is attempted at all
 * (`orderConfirmation.required`) and what counts as settled once it is.
 *
 * Three things it deliberately does not do:
 *
 * - **It does not skip the add-on.** `skipAddon` is left at its schema default
 *   of `false`, because the add-on *is* the tenant integration — it calls the
 *   client's system and sets the status this whole flow branches on.
 * - **It does not redirect.** `postStephook` signals the flow that the order
 *   exists (D-10) and returns; where the shopper goes next is the confirmation
 *   screen's button (D-4).
 * - **It does not treat exhausting the poll budget as a failure** (D-9). The
 *   order exists — `createOrder` returned it — and only the confirmation
 *   outcome is unknown.
 *
 * Cancellation is two refs answering two different questions.
 * `requestIdRef` asks whether the shopper asked for something else, and
 * `pollTimerRef` names the one tick there is to cancel. Both per hook
 * instance, exactly as `useGetOrderDetail` does it and with the same
 * consequence: one instance cannot stop another's poll.
 *
 * **Unmounting cancels nothing, and that is the fix (COM-818).** There is no
 * cleanup effect, because both halves of the one that used to be here were
 * harmful. Bumping `requestIdRef` discarded a `createOrder` that was merely
 * slower than the unmount: the store lives above `<Routes>`, so the
 * outcome still had a screen to land on, but `settle` never ran and
 * `creationStatus` stayed `'creating'` under a spinner — over an order the
 * server had already made. Calling `stopPolling()` stranded a chain that had
 * already armed: the one pending tick was cancelled, so nothing ticked again
 * and nothing reported, and `creationStatus` stayed `'confirming'` for good.
 * Neither was an edge case, but the unmount that caused them was not the one
 * named here. Until COM-858, `routes/index.tsx` gated route *registration* on
 * the global `loading` flag, so the `CREATE_ORDER` spinner de-registered every
 * route and react-router deleted the cart container within the same commit as
 * the CTA — `pushView` never ran at all. COM-858 narrows that gate to the boot
 * loaders, so from now on it *is* the navigation: `pushView` fires within a
 * frame of the CTA while a create takes hundreds of milliseconds. Either way
 * the unmount precedes the outcome on this flow, which is what makes the
 * absent cleanup load-bearing.
 *
 * An orphaned chain is bounded by the budget it already had: `pollTick` tests
 * `timeout` and `maxAttempts` on every tick and dispatches
 * `setOrderOutcomeUnknown()` once either is spent. So it reports and stops on
 * its own inside `timeout` — 60s unconfigured — and gets that whole window to
 * return a real `confirmed`/`created`/`failed` first, which is the outcome the
 * shopper is actually owed.
 *
 * Two accepted costs, named rather than solved:
 *
 * - **No individual request is bounded** while COM-772 is open, and the budget
 *   is tested *per tick*, so one hung read can carry an orphan past its
 *   deadline.
 * - **An orphan cannot be superseded from another instance.** The token is a
 *   `useRef`, so a second attempt begun while an orphan is still ticking can
 *   have its slice writes overwritten by the first. Bounding that needs a
 *   store-level token; the window is one `timeout` and the sequence — place an
 *   order, return to the cart, place another inside 60s — is rare enough to
 *   record here instead.
 */
export const useOrderConfirmation = () => {
  const dispatch = useAppDispatch()
  const sessionUid = useAppSelector((state) => state.defaultSlice.sessionId)
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const orderConfirmation = useAppSelector(
    (state) =>
      state.defaultSlice.sessionData?.configuration?.order?.confirmation,
  )

  /**
   * Reports an `orderConfirmation` block that is authored and inert.
   *
   * On this surface rather than only on Order Detail because this is where a
   * misconfigured block first costs a shopper something: the CTA polls to its
   * deadline and reports an outcome it never observed. Runs for every
   * storefront, opted in or not, and fires nothing when the block is absent —
   * which is the common case.
   */
  useOrderConfirmationConfigGuards(FUNCTION_NAME)

  const [createOrderFn] = useCreateOrderMutation()
  const [confirmOrderFn] = useConfirmOrderMutation()
  // `network-only` for the reason `useGetOrderDetail` gives: the point of
  // polling is to see the order change, and the normalised cache would replay
  // the first response forever.
  const [getOrderFn] = useLazyGetOrder({ fetchPolicy: 'network-only' })

  /**
   * Monotonic token for the newest attempt. See `useGetOrderDetail`.
   *
   * Bumped at exactly two sites — a new attempt, and `resetOrderConfirmation`
   * — and deliberately **not** by unmounting. What it answers is "did the
   * shopper ask for something else?", and leaving the screen is not that. The
   * confirm dialog's "yes" emits `createOrder`, `closeDialog` and
   * `placeOrderFlowNext` from one un-awaited `processActions` reduce, so the
   * cart container comes down while the create is still open — before COM-858
   * because the `CREATE_ORDER` loader de-registered the route table, and after
   * it because `usePlaceOrderFlow` navigates. A token bumped on the way out
   * would therefore supersede every attempt on the plain synchronous checkout,
   * which is what COM-818 was.
   */
  const requestIdRef = useRef(0)
  /** The one scheduled tick, and the re-entrancy guard for polling. */
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * When the attempt started, against which `timeout` is measured.
   *
   * Set at the CTA, **before** `createOrder` goes out, so the budget covers
   * the shopper's whole wait rather than the polling part of it. That is what
   * the field means — milliseconds of wall clock before the client gives up —
   * and the alternative starts the clock at an unpredictable moment, making
   * the total wait `create + confirm + timeout` and the configured number
   * unrelated to anything a shopper experiences.
   *
   * Stored raw and compared per tick, never as a precomputed deadline: that is
   * the defect COM-743 shipped, and the reasoning transfers even though the
   * session config is reliably present by checkout here.
   */
  const pollStartedAtRef = useRef(0)
  /**
   * Poll ticks issued for this attempt, against `maxAttempts`.
   *
   * Ticks only. The `createOrder` and `confirmOrder` round trips are not
   * attempts at reading the outcome, and counting them would spend a budget
   * authored for the wait against the work that precedes it.
   */
  const pollAttemptsRef = useRef(0)
  /**
   * The raw confirmation config, re-read every render and never captured.
   *
   * The reasoning is `useGetOrderDetail`'s and so is the mechanism, but the
   * urgency is not: this flow starts at a cart CTA, and a cart cannot render
   * before `getSession` has resolved, so the block is reliably present by the
   * time anything here runs. Kept as a ref anyway, because the cost is one
   * line and the failure it prevents is silent — stale values that are real
   * and simply wrong.
   */
  const configRef = useRef(orderConfirmation)
  configRef.current = orderConfirmation

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  /** True when a newer attempt started while this one was in flight. */
  const isSuperseded = (requestId: number) => requestIdRef.current !== requestId

  /**
   * Writes the terminal verdict. `pending` is not one, and cannot arrive.
   *
   * The order travels with the failure as well as the successes. A status
   * verdict is reached by *reading* an order, so that order is the one the
   * screen has to show; omitting it would leave the slice holding the previous
   * tick's copy under a `failed` status. No `message` is set here — the reason
   * is the order's own status, which the screen already has, and inventing a
   * string for it would put server vocabulary where configured copy belongs.
   */
  const settle = (
    order: IOrderDetail,
    outcome: Exclude<OrderOutcome, 'pending'>,
  ) => {
    if (outcome === 'confirmed') {
      dispatch(setOrderConfirmed(order))
      return
    }
    if (outcome === 'failed') {
      dispatch(setOrderCreationFailed({ order }))
      return
    }
    dispatch(setOrderCreated(order))
  }

  /**
   * One poll tick, which schedules the next unless the order has settled.
   *
   * No failure ends the attempt: a dropped tick leaves the order as the server
   * last described it, the screen is already saying "confirming", and the
   * budget is what stops this. That is why there is no poll-error action on
   * the write half — see `setOrderCreationPollResult`.
   */
  const pollTick = async (
    orderId: string,
    requestId: number,
  ): Promise<void> => {
    // This tick's timer has fired, so the ref no longer names anything
    // cancellable. Leaving the spent id would make a later `stopPolling` look
    // effective while cancelling nothing.
    pollTimerRef.current = null
    if (isSuperseded(requestId)) return

    // Counted before the request, so `maxAttempts: 1` means exactly one tick.
    pollAttemptsRef.current += 1
    try {
      const result = await getOrderFn({
        variables: {
          storefrontName: storefrontName || '',
          sessionUid: sessionUid as string,
          id: orderId,
        },
      })
      if (result.error) throw result.error
      if (isSuperseded(requestId)) return

      const order = result.data?.order
      if (order) {
        const outcome = deriveOrderOutcome(order, configRef.current)
        if (outcome !== 'pending') {
          settle(order, outcome)
          return
        }
        dispatch(setOrderCreationPollResult(order))
      }
    } catch (exception) {
      if (isSuperseded(requestId)) return
      handleOrdersFetchError(
        exception,
        FUNCTION_NAME,
        'order confirmation poll tick failed',
      )
    }

    const { pollingTime, maxAttempts, timeout } = resolveOrderConfirmation(
      configRef.current,
    )
    if (
      Date.now() - pollStartedAtRef.current >= timeout ||
      pollAttemptsRef.current >= maxAttempts
    ) {
      // Unknown, never failed. The order exists and the shopper is owed a
      // refresh rather than an apology.
      dispatch(setOrderOutcomeUnknown())
      return
    }

    pollTimerRef.current = setTimeout(() => {
      pollTick(orderId, requestId)
    }, pollingTime)
  }

  /**
   * Arms the safety net's **first** look, at `ORDER_POLL_FIRST_LOOK_MS`
   * (COM-875). See that constant for why 500, why a timer rather than a direct
   * call, and what the shifted tick grid trades away. All four callers below
   * route through here; `pollTick`'s own tail still schedules at `pollingTime`,
   * so the cadence is unchanged.
   *
   * Site-local, and the half most easily got wrong: **the deadline does not
   * move with the first look.** `pollStartedAtRef` is set at the CTA, before
   * `createOrder` goes out, and nothing here touches it. This tick is a real
   * tick — `pollTick` increments `pollAttemptsRef` before its request — and it
   * *replaces* the tick that used to be first rather than being added in front
   * of it, so `maxAttempts: 1` still buys exactly one read.
   *
   * The two bounds do not shift alike, and "the budget is unchanged" is only
   * true of one of them: an attempt-bounded chain now spends its last attempt
   * `pollingTime - 500` **earlier** in wall clock, so a slow order can report
   * `unknown` where it once reported `confirmed`; a deadline-bounded chain
   * fits one more tick inside the same `timeout`. Neither is reachable until a
   * storefront can author the block, so treat both as newly exposed rather
   * than as proven.
   */
  const armPoll = (orderId: string, requestId: number) => {
    pollTimerRef.current = setTimeout(() => {
      pollTick(orderId, requestId)
    }, ORDER_POLL_FIRST_LOOK_MS)
  }

  /**
   * Signals the flow that the order exists, and never fails the attempt.
   *
   * The order was created before this ran, so a stephook that does not answer
   * is a conversation left un-advanced — worth a Datadog record and worth
   * nothing else. Reporting it as a creation failure would tell a shopper
   * their order did not happen while it sits in the database.
   */
  const signalFlow = async (session: SessionDataArgs, phone: string) => {
    try {
      await postStephook({
        data: session,
        phoneNumber: phone,
        workflowApi: process.env.REACT_APP_WORKFLOW_API || '',
        botrunnerApi: process.env.REACT_APP_BOTRUNNER_API || '',
      })
    } catch (exception) {
      handleOrdersFetchError(
        exception,
        FUNCTION_NAME,
        'order created but the stephook was not signalled',
      )
    }
  }

  /**
   * Confirms one created order, and decides what an error means.
   *
   * Three outcomes, and the distinction between the last two is the whole
   * reason this is not a plain try/catch:
   *
   * - It returns the order, normally `CONFIRMED`, and the attempt is terminal.
   * - A `400` is **definite**, with one exception: the client system declined,
   *   a checkout rule was not satisfied, or the payload did not validate. The
   *   add-on runs `Instead` of the native confirm and asks the client system
   *   first, so a decline means nothing was changed. Terminal failure, with
   *   the server's own description kept for support.
   * - The exception is `isAmbiguousOrderMatchError` — the `$or` filter matched
   *   more than one confirmable order, which says the server could not tell
   *   which order was meant and nothing about whether this one can be
   *   confirmed. It polls; the helper carries the mechanism.
   * - Anything else is **ambiguous**, so it polls. The native confirm's own
   *   throws arrive as `409 ERROR UPDATING`, because `confirmOrder` wraps its
   *   catch in `ERROR.UPDATE` and `pushError` only overrides that for a value
   *   that is already an `InternalError` — so a bare throw from
   *   `discountStock`, `sendProtobufEvent` or mongo lands here, by which time
   *   the order may already have moved. A `500` is the add-on's transport
   *   failure to the client system. Declaring failure in either case would
   *   risk contradicting the database.
   *
   * Note what is **not** in the definite bucket: "the order was not in a
   * confirmable state" produces *zero* matches, so it raises `404`, not `400`,
   * and lands in the ambiguous half — correctly, since a status the filter
   * rejects may be one the order is passing through.
   */
  const confirmOrder = async (order: IOrderDetail, requestId: number) => {
    dispatch(setOrderConfirming(order))
    try {
      const result = await confirmOrderFn({
        variables: {
          storefrontName: storefrontName || '',
          sessionUid: sessionUid as string,
          id: order.id,
        },
      })
      if (isSuperseded(requestId)) return

      const confirmed = result.data?.confirmOrder
      if (!confirmed) {
        // A 200 carrying no order is a transport anomaly, not a decline, and
        // the order it was about still exists. Ambiguous, so poll.
        armPoll(order.id, requestId)
        return
      }

      const outcome = deriveOrderOutcome(confirmed, configRef.current)
      if (outcome !== 'pending') {
        settle(confirmed, outcome)
        return
      }
      // Confirmed without reaching a terminal status — the safety net's
      // remaining job.
      dispatch(setOrderCreationPollResult(confirmed))
      armPoll(order.id, requestId)
    } catch (exception) {
      // Every failure arrives here, and that is a property of the mutation
      // hooks: under the default `errorPolicy` the mutate function *rejects*,
      // so `FetchResult.errors` is never the route a rejection takes. Setting
      // `errorPolicy: 'all'` or an `onError` on `useConfirmOrderMutation`
      // would make it resolve instead, and this branch — where a decline is
      // told apart from an ambiguity — would stop running. The hooks say so.
      if (isSuperseded(requestId)) return
      handleOrdersFetchError(
        exception,
        FUNCTION_NAME,
        'order confirmation failed',
      )
      if (
        isBadRequestError(exception) &&
        !isAmbiguousOrderMatchError(exception)
      ) {
        dispatch(
          setOrderCreationFailed({
            message: getServerErrorDescription(exception),
          }),
        )
        return
      }
      armPoll(order.id, requestId)
    }
  }

  /**
   * The whole flow: create, branch, confirm where applicable, poll otherwise.
   *
   * Returns once the outcome is either terminal or being polled for — the
   * polling itself continues on a timer, the way `fetchOrderDetail` arms one
   * and returns. That is what lets the caller take its loader down without
   * leaving the shopper under a spinner for the length of a callback wait.
   */
  const createAndConfirmOrder = async ({
    session,
    phone,
  }: {
    session: SessionDataArgs
    phone: string
  }): Promise<void> => {
    stopPolling()
    const requestId = (requestIdRef.current += 1)
    pollStartedAtRef.current = Date.now()
    pollAttemptsRef.current = 0
    dispatch(setOrderCreating())

    if (!sessionUid || !storefrontName) {
      // Not reachable from a rendered cart — both come off the URL before
      // anything fetches — but the mutation declares `sessionUid: ID!` and a
      // request without one is a variable error rather than an answer. Failing
      // here at least leaves the screen in a state that says so.
      handleOrdersFetchError(
        new Error('missing session or storefront'),
        FUNCTION_NAME,
        'order creation skipped',
      )
      dispatch(setOrderCreationFailed())
      return
    }

    let created: IOrderDetail
    try {
      const result = await createOrderFn({
        variables: { storefrontName, sessionUid },
      })
      if (isSuperseded(requestId)) return

      const order = result.data?.createOrder
      if (!order) {
        // A 200 carrying no order is a transport or schema anomaly, not a
        // decline — and the order may well exist server-side, which makes this
        // the most expensive state in the flow to leave unobserved. It was the
        // only failure dispatch here that reported nothing.
        handleOrdersFetchError(
          new Error('createOrder returned no order'),
          FUNCTION_NAME,
          'order creation returned no order',
        )
        dispatch(setOrderCreationFailed())
        return
      }
      created = order
    } catch (exception) {
      if (isSuperseded(requestId)) return
      handleOrdersFetchError(exception, FUNCTION_NAME, 'order creation failed')
      // A checkout-rule rejection is a `400` whose `extras` name the
      // unsatisfied rules, and it is the reason the webview has to be the
      // caller: `validateCheckoutSessionRule` runs inside `getOrderSplitter`,
      // so a flow-created order fails the same rules where no client can see
      // it. Surfaced as rule types for the screen to route on, never as raw
      // error text.
      dispatch(
        setOrderCreationFailed({
          message: getServerErrorDescription(exception),
          failedCheckoutRules: getFailedCheckoutRules(exception),
        }),
      )
      return
    }

    // After a successful create, and before any confirmation: what the flow is
    // being told is that the order exists, not that it settled (D-10).
    await signalFlow(session, phone)
    if (isSuperseded(requestId)) return

    const outcome = deriveOrderOutcome(created, configRef.current)
    if (outcome !== 'pending') {
      settle(created, outcome)
      return
    }

    if (CONFIRMABLE_STATUSES.includes(created.status ?? '')) {
      await confirmOrder(created, requestId)
      return
    }

    // `WAITING_CALLBACK` lands here: the client system will call back, so
    // there is nothing to confirm and everything to wait for.
    dispatch(setOrderConfirming(created))
    armPoll(created.id, requestId)
  }

  /**
   * Leaves the confirmation flow: stops the poll and resets the write half.
   *
   * The token is bumped as well as the timer cleared, so a tick already
   * awaiting a response cannot write the order back after the reset.
   */
  const resetOrderConfirmation = () => {
    stopPolling()
    requestIdRef.current += 1
    dispatch(clearOrderCreation())
  }

  return { createAndConfirmOrder, resetOrderConfirmation }
}

export default useOrderConfirmation
